import type { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { useCallback, useEffect, useState } from "react";
import type {
  ChecklistComponent,
  ComponentOption,
  ComponentType,
  PricingType,
} from "@/types/components";

interface UseComponentFormStateProps {
  component: ChecklistComponent;
}

export function useComponentFormState({ component }: UseComponentFormStateProps) {
  // Form state
  const [componentType, setComponentType] = useState<ComponentType>(component.component_type);
  const [label, setLabel] = useState("");
  const [placeholder, setPlaceholder] = useState("");
  const [helpText, setHelpText] = useState("");
  const [isRequired, setIsRequired] = useState(false);
  const [defaultValue, setDefaultValue] = useState("");
  const [basePrice, setBasePrice] = useState<string>("0");
  const [pricingType, setPricingType] = useState<PricingType>("fixed");
  const [pricingUnitLabel, setPricingUnitLabel] = useState("");
  const [allowQuantityInput, setAllowQuantityInput] = useState(false);
  const [options, setOptions] = useState<ComponentOption[]>([]);
  const [newOptionLabel, setNewOptionLabel] = useState("");
  const [newOptionPrice, setNewOptionPrice] = useState<string>("0");

  // Load component data into form
  useEffect(() => {
    if (component) {
      setComponentType(component.component_type);
      setLabel(component.label);
      setPlaceholder(component.placeholder || "");
      setHelpText(component.help_text || "");
      setIsRequired(component.is_required);
      setDefaultValue(component.default_value || "");
      setBasePrice((component.base_price || 0).toString());
      setPricingType((component.pricing_type || "fixed") as PricingType);
      setPricingUnitLabel(component.pricing_unit_label || component.unit_label || "");
      setAllowQuantityInput(component.allow_quantity_input);
      setOptions(component.options || []);
    }
  }, [component]);

  // Option management
  const addOption = useCallback(() => {
    if (!newOptionLabel.trim()) {
      return;
    }

    const newOption: ComponentOption = {
      option_label: newOptionLabel.trim(),
      option_value: newOptionLabel
        .toLowerCase()
        .replace(/\s+/g, "_")
        .replace(/[^a-z0-9_]/g, ""),
      price_modifier: Number.parseFloat(newOptionPrice) || 0,
      is_default: options.length === 0,
      display_order: options.length,
    };

    setOptions([...options, newOption]);
    setNewOptionLabel("");
    setNewOptionPrice("0");
  }, [newOptionLabel, newOptionPrice, options]);

  const removeOption = useCallback(
    (index: number) => {
      const newOptions = options.filter((_, i) => i !== index);
      if (options[index].is_default && newOptions.length > 0) {
        newOptions[0].is_default = true;
      }
      setOptions(newOptions);
    },
    [options]
  );

  const setDefaultOption = useCallback(
    (index: number) => {
      const newOptions = options.map((option, i) => ({
        ...option,
        is_default: i === index,
      }));
      setOptions(newOptions);
    },
    [options]
  );

  const updateOptionPrice = useCallback(
    (index: number, price: number) => {
      const newOptions = [...options];
      newOptions[index] = { ...newOptions[index], price_modifier: price };
      setOptions(newOptions);
    },
    [options]
  );

  const updateOptionLabel = useCallback(
    (index: number, newLabel: string) => {
      const newOptions = [...options];
      newOptions[index] = {
        ...newOptions[index],
        option_label: newLabel,
        option_value: newLabel
          .toLowerCase()
          .replace(/\s+/g, "_")
          .replace(/[^a-z0-9_]/g, ""),
      };
      setOptions(newOptions);
    },
    [options]
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setOptions((items) => {
        const oldIndex = items.findIndex(
          (item) => (item.id ?? item.option_value ?? `option-${items.indexOf(item)}`) === active.id
        );
        const newIndex = items.findIndex(
          (item) => (item.id ?? item.option_value ?? `option-${items.indexOf(item)}`) === over.id
        );

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }, []);

  // Build form data for submission
  const getFormData = useCallback(() => {
    const updateData: any = {
      label: label.trim(),
      placeholder: placeholder.trim() || undefined,
      help_text: helpText.trim() || undefined,
      is_required: isRequired,
      default_value: defaultValue.trim() || undefined,
      base_price: Number.parseFloat(basePrice) || 0,
      pricing_type: pricingType,
      pricing_unit_label: pricingUnitLabel.trim() || undefined,
      allow_quantity_input: allowQuantityInput,
    };

    // Allow component type change
    if (componentType !== component.component_type) {
      updateData.component_type = componentType;
    }

    // Add options only for component types that support them
    if (["select", "radio", "checkbox"].includes(componentType)) {
      updateData.options = options.map((option, index) => ({
        ...option,
        option_label: option.option_label,
        option_value:
          option.option_value ||
          option.option_label
            .toLowerCase()
            .replace(/\s+/g, "_")
            .replace(/[^a-z0-9_]/g, ""),
        price_modifier: option.price_modifier || 0,
        is_default: option.is_default,
        display_order: index,
      }));
    }

    return updateData;
  }, [
    label,
    placeholder,
    helpText,
    isRequired,
    defaultValue,
    basePrice,
    pricingType,
    pricingUnitLabel,
    allowQuantityInput,
    componentType,
    component.component_type,
    options,
  ]);

  // Validation
  const isValid = useCallback(() => {
    return label.trim().length > 0;
  }, [label]);

  return {
    // Form state
    componentType,
    setComponentType,
    label,
    setLabel,
    placeholder,
    setPlaceholder,
    helpText,
    setHelpText,
    isRequired,
    setIsRequired,
    defaultValue,
    setDefaultValue,
    basePrice,
    setBasePrice,
    pricingType,
    setPricingType,
    pricingUnitLabel,
    setPricingUnitLabel,
    allowQuantityInput,
    setAllowQuantityInput,

    // Options state
    options,
    newOptionLabel,
    setNewOptionLabel,
    newOptionPrice,
    setNewOptionPrice,

    // Option management
    addOption,
    removeOption,
    setDefaultOption,
    updateOptionPrice,
    updateOptionLabel,
    handleDragEnd,

    // Form submission
    getFormData,
    isValid,
  };
}
